import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, NgZone, OnInit } from '@angular/core';
import { HomeTabs, Search } from '@local/client-contracts';
import { PopupService } from '@local/ui-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ContextMenuService } from '@shared/components';
import { EventsService } from '@shared/services';
import { KeyboardService } from '@shared/services/keyboard.service';
import { RouterService } from '@shared/services/router.service';
import { SessionService } from '@shared/services/session.service';
import { cloneDeep } from 'lodash';
import { WorkspacesService } from 'src/app/bar/services';
import { ResultCommandService } from 'src/app/bar/services/commands/result-command.service';
import { FavoritesService } from 'src/app/bar/services/favorites.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchService } from 'src/app/bar/services/search';
import { SearchParamsService } from 'src/app/bar/services/search-params.service';
import { VisitsSourceSettings } from 'src/app/bar/services/search/client/visits';
import { TopPicksResult } from '../../../results';
import { InvokeCommand } from '../../../results/models/invoke-command.model';
import { WidgetsService } from '../../services/widgets.service';
import { WidgetBaseComponent } from '../widget-base.component';
import { AvatarListService } from 'src/app/bar/services/avatar-list.service';
import { HomeTabsService } from 'src/app/bar/services/home-tabs.service';

@UntilDestroy()
@Component({
  selector: 'recents-widget',
  templateUrl: './recents-widget.component.html',
  styleUrls: ['./recents-widget.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecentsWidgetComponent extends WidgetBaseComponent<HomeTabs.Widget> implements OnInit {
  constructor(
    protected elementRef: ElementRef,
    protected eventsService: EventsService,
    protected hubService: HubService,
    protected searchService: SearchService,
    protected cdr: ChangeDetectorRef,
    protected keyboardService: KeyboardService,
    protected widgetsService: WidgetsService,
    private resultCommandService: ResultCommandService,
    protected ngZone: NgZone,
    private favoritesService: FavoritesService,
    protected session: SessionService,
    protected contextMenuService: ContextMenuService,
    protected workspacesService: WorkspacesService,
    protected popupService: PopupService,
    protected routerService: RouterService,
    private searchParamsService: SearchParamsService,
    protected homeTabsService: HomeTabsService,
    protected avatarListService: AvatarListService,
  ) {
    super(
      elementRef,
      cdr,
      keyboardService,
      widgetsService,
      ngZone,
      session,
      contextMenuService,
      workspacesService,
      popupService,
      eventsService,
      routerService,
      homeTabsService,
      avatarListService
    );
    this.searchSession = this.searchService.getOrCreateSearchSession('topPicksWidget');
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.pageType = 'recently_viewed';
    this.listenToFavoriteChange();
  }

  listenToFavoriteChange() {
    this.favoritesService.all$.pipe(untilDestroyed(this)).subscribe((res) => {
      if (!res) res = [];
      if (this.displayedContext?.items?.length > 0) {
        this.displayedContext.items.forEach((recently: Search.ResultItem, index: number) => {
          const found = res.find((favorite) => recently.id === favorite.id);
          const prev_favorite_value = recently.isFavorite;
          recently.isFavorite = !!found;
          if (prev_favorite_value !== recently.isFavorite) {
            this.resultElementsComponents['_results'][index]?.cdr.markForCheck();
          }
        });
        this.cdr.markForCheck();
      }
    });
  }

  loadData() {
    this.isLoading = true;
    const options: SearchOptions = {
      resetSession: true,
      trigger: 'recentlyViewed_widget_load',
      sources: [
        {
          type: 'visits',
          cache: 'first',
          defaultSort: 'recent-first',
          includeInteractions: 'wait',
          includeTraits: 'wait',
          excludeTypes: ['Email', 'Event', 'Channel'],
          maxCount: 20,
          tag: 'recently-viewed-widget',
        } as VisitsSourceSettings,
      ],
    };
    this.searchSession
      .search$(options)
      .pipe(untilDestroyed(this))
      .subscribe(async (ctx) => {
        if (ctx.searchCompleted) {
          this.displayedContext = cloneDeep(ctx);
          this.displayedContext.items = ctx.items
            .filter((elm) => elm.type == 'result')
            .map((r) => {
              (<TopPicksResult>r).highlights = [];
              return r;
            });
          if (this.displayedContext.items.length || ctx.searchCompleted) this.isLoading = false;
          await this.onResizeChange();
          this.cdr.markForCheck();
        }
      });
  }

  async navigate() {
    this.eventsService.event('home_page.widget_title_see_all', { label: this.pageType });
    await this.routerService.navigateByUrl('search');
    this.searchParamsService.addGroup({ name: 'visits', title: 'Recently Viewed' });
  }

  public async onInvoke(data: InvokeCommand, type: Search.ResultType): Promise<void> {
    const items = this.displayedContext.items;
    const eventInfo = { ...data.eventInfo, resources: [{ list: 'recently_viewed' }] };
    data = { ...data, eventInfo };
    this.resultCommandService.onInvoke(
      items,
      data,
      type,
      this.displayedContext.sessionId,
      this.displayedContext.clientSearchId,
      'recents-widget',
      this.selectedIndex,
      this.displayedContext.lastHeaderIndex,
      undefined,
      undefined,
      'recently_viewed'
    );
  }
}
